import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, NavLink, Route, Routes, useLocation, Link } from 'react-router';
import { motion } from 'motion/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { syncPushSubscription } from './lib/push.ts';
import { InstallPrompt } from './components/InstallPrompt.tsx';
import { useAutoReveal } from './lib/fairness.ts';
import { t, duration, tDynamic } from './i18n/index.ts';
import { api } from './lib/api.ts';
import { useLiveUpdates, useMaintenance, useSession } from './lib/session.tsx';
import { Icon, Loading, useNow } from './components/ui.tsx';
import type { homeView } from '../server/domain/home.ts';
import { InviteAccept, LinkSignIn, Login, MfaPage } from './views/Auth.tsx';
import { Home } from './views/Home.tsx';
import { TournamentDetail, TournamentLayout, TournamentList } from './views/Tournaments.tsx';
import { TablePage } from './views/Table.tsx';
import { HandDetailPage, ResultsPage } from './views/Results.tsx';
import { DebtsPage } from './views/Debts.tsx';
import { ChatPage } from './views/Chat.tsx';
import { NotificationsPage } from './views/Notifications.tsx';
import { ProfilePage } from './views/Profile.tsx';
import { StatsPage } from './views/Stats.tsx';import { useThemeSync } from './lib/theme.ts';


const AdminRoutes = lazy(() => import('./views/admin/AdminRoutes.tsx'));
const TournamentEditor = lazy(() => import('./views/admin/TournamentAdmin.tsx').then((m) => ({ default: m.TournamentEditor })));

export type HomeData = ReturnType<typeof homeView>;

export function useHome(enabled = true) {
  return useQuery({ queryKey: ['home'], queryFn: () => api.get<HomeData>('/api/home'), enabled, refetchInterval: 60_000 });
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { me, loading } = useSession();
  const location = useLocation();
  if (loading) return <Loading />;
  if (!me) return <Navigate to={`/kirjaudu?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  if (!me.mfaVerified) return <Navigate to={`/mfa?next=${encodeURIComponent(location.pathname)}`} replace />;
  return <>{children}</>;
}

/** Admin pages exist only for admins: anyone else gets the ordinary "not found" page. */
function RequireAdmin({ children }: { children: ReactNode }) {
  const { me } = useSession();
  if (me?.role !== 'superadmin') return <NotFound />;
  return <>{children}</>;
}

function NotFound() {
  return (
    <div className="page-narrow not-found">
      <p className="not-found-mark" aria-hidden="true">
        ♠
      </p>
      <h1>{t('app.notFoundTitle')}</h1>
      <p className="hint">{t('app.notFoundBody')}</p>
      <Link className="btn btn-primary" to="/">
        {t('app.toHome')}
      </Link>
    </div>
  );
}

function TurnBanner({ home, receivedAt }: { home: HomeData | undefined; receivedAt: number }) {
  const location = useLocation();
  const now = useNow(home ? home.serverTime - receivedAt : 0, 30_000);
  const turn = home?.myTurns.find((m) => m.tournamentStatus === 'running');
  if (!turn || location.pathname === '/' || location.pathname.startsWith(`/turnaukset/${turn.tournamentId}/poyta`)) return null;
  return (
    <div className="turn-banner">
      <Icon name="clock" />
      <span>
        <strong>{t('home.yourTurn')}</strong> {turn.tournamentName}
        {turn.deadlineAt ? `, ${t('table.timeLeft', { time: duration(turn.deadlineAt - now) })}` : ''}
      </span>
      <Link className="btn btn-brass btn-small" to={`/turnaukset/${turn.tournamentId}/poyta`}>
        {t('home.act')}
      </Link>
    </div>
  );
}

/** Pages animate in when the section changes; tabs inside a tournament keep their frame. */
/** A count badge that screen readers hear as words ("2 lukematonta"), not a bare number. */
function NavCount({ to, n }: { to: string; n: number }) {
  const text = to === '/' ? t('nav.countTurns', { n }) : to === '/profiili' ? t('nav.countNotifications', { n }) : t('nav.countUnread', { n });
  return (
    <span className="count">
      <span aria-hidden="true">{n}</span>
      <span className="visually-hidden">{text}</span>
    </span>
  );
}

const TITLES: [string, string][] = [
  ['/turnaukset', 'nav.tournaments'],
  ['/velat', 'nav.debts'],
  ['/tilastot', 'nav.stats'],
  ['/keskustelu', 'nav.chat'],
  ['/ilmoitukset', 'nav.notifications'],
  ['/profiili', 'nav.profile'],
  ['/yllapito', 'nav.admin'],
];

/** "Velat – HimaPokeri": each section has its own browser tab title. */
function useDocumentTitle(pathname: string) {
  useEffect(() => {
    const match = TITLES.find(([prefix]) => pathname.startsWith(prefix));
    document.title = match ? `${tDynamic(match[1])} – ${t('app.name')}` : t('app.name');
  }, [pathname]);
}

function sectionKey(pathname: string): string {
  const m = /^\/turnaukset\/[^/]+/.exec(pathname);
  if (m) return m[0];
  // Switching chat channels or admin tabs stays within the section: no page animation.
  const section = /^\/(keskustelu|yllapito)/.exec(pathname);
  return section ? section[0] : pathname;
}

function Shell({ children, wide }: { children: ReactNode; wide?: boolean }) {
  const { me } = useSession();
  const location = useLocation();
  const connection = useLiveUpdates(!!me?.mfaVerified);
  const maintenance = useMaintenance();
  // The table shows the pause in its own status area.
  const onTable = /^\/turnaukset\/[^/]+\/poyta/.test(location.pathname);
  useDocumentTitle(location.pathname);
  // Keep this device's push subscription registered for the signed-in user.
  useEffect(() => {
    if (me?.mfaVerified) void syncPushSubscription();
  }, [me?.id, me?.mfaVerified]);
  // Reveal this device's fairness seeds for tournaments that have started.
  useAutoReveal(me?.mfaVerified ? me.id : undefined);
  const home = useHome(!!me?.mfaVerified);
  const qc = useQueryClient();
  const turnCount = home.data?.myTurns.filter((m) => m.tournamentStatus === 'running').length ?? 0;
  const unreadChat = home.data?.unreadChat ?? 0;
  const unreadNotes = home.data?.unreadNotifications ?? 0;
  const logout = async () => {
    await api.post('/api/auth/logout').catch(() => undefined);
    qc.clear();
    window.location.href = '/kirjaudu';
  };
  const nav = [
    { to: '/', icon: 'home', label: t('nav.home'), count: turnCount, end: true },
    { to: '/turnaukset', icon: 'trophy', label: t('nav.tournaments') },
    { to: '/velat', icon: 'ledger', label: t('nav.debts') },
    { to: '/tilastot', icon: 'chart', label: t('nav.stats') },
    { to: '/keskustelu', icon: 'chat', label: t('nav.chat'), count: unreadChat },
    { to: '/ilmoitukset', icon: 'bell', label: t('nav.notifications'), count: unreadNotes },
  ];
  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        {t('app.skip')}
      </a>
      <nav className="sidenav" aria-label={t('nav.main')}>
        <Link to="/" className="brand">
          {/* <img className="brand-mark" src="/favicon.svg" alt="" width={30} height={30} /> */}
          {t('app.name')}
        </Link>
        {nav.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} className="navlink">
            <Icon name={n.icon} />
            {n.label}
            {!!n.count && <NavCount to={n.to} n={n.count} />}
          </NavLink>
        ))}
        <NavLink to="/profiili" className="navlink">
          <Icon name="user" />
          {t('nav.profile')}
        </NavLink>
        <div className="sidenav-footer">
          {me?.role === 'superadmin' && (
            <NavLink to="/yllapito" className="navlink">
              <Icon name="shield" />
              {t('nav.admin')}
            </NavLink>
          )}
          <button className="navlink btn-ghost" style={{ border: 0, background: 'none', font: 'inherit', cursor: 'pointer' }} onClick={logout}>
            <Icon name="logout" />
            {t('nav.logout')}
          </button>
        </div>
      </nav>
      <div>
        {maintenance && !onTable && (
          <div className="conn-banner" role="status">
            {t('app.maintenance')}
          </div>
        )}
        {/* Losing the connection is expected during an update; the banner above already says so. */}
        {connection === 'offline' && !maintenance && (
          <div className="conn-banner" role="alert">
            {t('app.offline')}
          </div>
        )}
        {connection === 'reconnected' && (
          <div className="conn-banner" role="status" style={{ background: 'var(--ok-soft)', borderColor: 'var(--ok)' }}>
            {t('app.reconnected')}
          </div>
        )}
        <main id="main" className={`main ${wide ? 'wide' : ''}`} tabIndex={-1}>
          {!wide && <TurnBanner home={home.data} receivedAt={home.dataUpdatedAt} />}
          <motion.div key={sectionKey(location.pathname)} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2, ease: 'easeOut' }}>
            {children}
          </motion.div>
        </main>
      </div>
      <InstallPrompt />
      <nav className="tabbar" aria-label={t('nav.main')}>
        {[
          ...nav.filter((n) => ['/', '/turnaukset', '/velat', '/keskustelu'].includes(n.to)),
          { to: '/profiili', icon: 'user', label: t('nav.profile'), count: unreadNotes, end: false },
        ].map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end}>
            <span className="tab-icon">
              <Icon name={n.icon} />
            </span>
            <span className="tab-label">{n.label}</span>
            {!!n.count && <NavCount to={n.to} n={n.count} />}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

const page = (el: ReactNode, wide = false) => (
  <RequireAuth>
    <Shell wide={wide}>{el}</Shell>
  </RequireAuth>
);

export function App() {
  useThemeSync();
  return (
    <Routes>
      <Route path="/kirjaudu" element={<Login />} />
      <Route path="/mfa" element={<MfaPage />} />
      <Route path="/kutsu/:token" element={<InviteAccept />} />
      <Route path="/kirjaudu/linkki" element={<LinkSignIn />} />
      <Route path="/" element={page(<Home />)} />
      <Route path="/turnaukset" element={page(<TournamentList />)} />
      {/* Any player can organize a tournament; the server checks who may edit which. */}
      <Route path="/turnaukset/uusi" element={page(<Suspense fallback={<Loading />}><div className="page"><TournamentEditor /></div></Suspense>)} />
      <Route path="/turnaukset/:id/muokkaa" element={page(<Suspense fallback={<Loading />}><div className="page"><TournamentEditor /></div></Suspense>)} />
      <Route path="/turnaukset/:id" element={page(<TournamentLayout />)}>
        <Route index element={<TournamentDetail />} />
        <Route path="poyta" element={<TablePage />} />
        <Route path="tulokset" element={<ResultsPage />} />
        <Route path="jaot/:no" element={<HandDetailPage />} />
      </Route>
      <Route path="/velat" element={page(<DebtsPage />)} />
      <Route path="/tilastot" element={page(<StatsPage />)} />
      <Route path="/keskustelu" element={page(<ChatPage />)} />
      <Route path="/keskustelu/:channelId" element={page(<ChatPage />)} />
      <Route path="/ilmoitukset" element={page(<NotificationsPage />)} />
      <Route path="/profiili" element={page(<ProfilePage />)} />
      <Route
        path="/yllapito/*"
        element={page(
          <RequireAdmin>
            <Suspense fallback={<Loading />}>
              <AdminRoutes />
            </Suspense>
          </RequireAdmin>,
        )}
      />
      <Route path="*" element={page(<NotFound />)} />
    </Routes>
  );
}
